import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Plane, ArrowLeft } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import AppLogo from '@/components/AppLogo';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <SEOHead
        title="Terms of Service"
        description="Terms of service for How Busy Is My Airport. Free travel tool, no warranties on third-party flight data."
        path="/terms"
      />
      {/* Header */}
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <div className="leading-tight min-w-0">
              <h1 className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900 leading-[1.15]">
                How Busy Is My Airport
              </h1>
            </div>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </motion.div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-slate-800 mb-2 text-center">Terms of Service</h1>
            <p className="text-slate-500 text-center mb-8">Last updated: February 2026</p>
          </motion.div>

          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-8 prose prose-slate max-w-none">
              
              <h2 className="text-xl font-semibold text-slate-800 mt-0">1. Acceptance of Terms</h2>
              <p className="text-slate-600">
                By accessing and using Airport Busyness Predictor ("the Service"), you accept and agree to be 
                bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">2. Description of Service</h2>
              <p className="text-slate-600">
                Airport Busyness Predictor provides predictions of airport crowd levels based on flight schedule 
                data. Our Service includes:
              </p>
              <ul className="text-slate-600">
                <li>Airport busyness predictions based on real-time flight data</li>
                <li>Recommended arrival times for travelers</li>
                <li>Flight schedule information for 331+ airports worldwide</li>
                <li>Travel tips and informational content</li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-800">3. Disclaimer of Warranties</h2>
              <p className="text-slate-600">
                <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</strong> 
                We make no guarantees regarding:
              </p>
              <ul className="text-slate-600">
                <li>The accuracy of crowd level predictions</li>
                <li>The completeness or timeliness of flight data</li>
                <li>Wait times at security checkpoints or gates</li>
                <li>Actual airport conditions at any given time</li>
              </ul>
              <p className="text-slate-600">
                Our predictions are estimates based on scheduled flight data and should be used as general 
                guidance only. Actual conditions may vary due to factors beyond our control, including but 
                not limited to weather, security events, flight delays, and special circumstances.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">4. Limitation of Liability</h2>
              <p className="text-slate-600">
                To the fullest extent permitted by law, Airport Busyness Predictor and its creators shall not 
                be liable for any indirect, incidental, special, consequential, or punitive damages, including 
                but not limited to:
              </p>
              <ul className="text-slate-600">
                <li>Missed flights or connections</li>
                <li>Additional travel expenses</li>
                <li>Lost time or inconvenience</li>
                <li>Any other damages arising from use of or reliance on our predictions</li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-800">5. User Responsibilities</h2>
              <p className="text-slate-600">By using our Service, you agree to:</p>
              <ul className="text-slate-600">
                <li>Use the Service for personal, non-commercial purposes only</li>
                <li>Not attempt to reverse engineer or extract data from our Service</li>
                <li>Not use automated systems to access the Service without permission</li>
                <li>Provide accurate feedback when submitting prediction accuracy reports</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-800">6. Intellectual Property</h2>
              <p className="text-slate-600">
                All content, features, and functionality of the Service, including but not limited to text, 
                graphics, logos, and software, are owned by Airport Busyness Predictor and are protected by 
                copyright, trademark, and other intellectual property laws.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">7. Third-Party Data</h2>
              <p className="text-slate-600">
                Our Service uses flight data from FlightAware and other third-party sources. We do not guarantee 
                the accuracy or availability of third-party data. Use of our Service is also subject to the 
                terms and conditions of our data providers.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">8. Advertising</h2>
              <p className="text-slate-600">
                The Service may display advertisements provided by third parties, including Google AdSense. 
                We are not responsible for the content of third-party advertisements. Your interactions with 
                advertisers are solely between you and the advertiser.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">9. Modifications to Service</h2>
              <p className="text-slate-600">
                We reserve the right to modify, suspend, or discontinue the Service at any time without notice. 
                We may also update these Terms of Service from time to time. Continued use of the Service after 
                changes constitutes acceptance of the new terms.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">10. Governing Law</h2>
              <p className="text-slate-600">
                These Terms shall be governed by and construed in accordance with the laws of the United States, 
                without regard to its conflict of law provisions.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">11. Severability</h2>
              <p className="text-slate-600">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will 
                continue in full force and effect.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">12. Contact Information</h2>
              <p className="text-slate-600">
                For questions about these Terms of Service, please contact us:
              </p>
              <ul className="text-slate-600">
                <li>Email: <a href="mailto:jamursch@gmail.com" className="text-blue-600 hover:underline">jamursch@gmail.com</a></li>
                <li>Website: <a href="https://howbusyismyairport.com" className="text-blue-600 hover:underline">howbusyismyairport.com</a></li>
              </ul>

            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center gap-6 mb-4">
            <Link to="/" className="text-slate-600 hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/about" className="text-slate-600 hover:text-blue-600 transition-colors">About</Link>
            <Link to="/blog" className="text-slate-600 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/privacy" className="text-slate-600 hover:text-blue-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-slate-600 hover:text-blue-600 transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Airport Busyness Predictor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
